import React, { useState } from 'react';
import Card from '../../Card';
import Button from '../../Button';
import DetailRow from '../../DetailRow';
import { Menu, MenuItem } from '../../Menu';
import ConfirmDialog from '../../ConfirmDialog';
import EditPencil from '../edit/EditPencil';
import ReadFailed from '../../ReadFailed';
import { CONTACT_NUMBERS, contactCardName } from '../../../domain/people';
import s from './tabs.module.css';
import { READING_WORDING as RW } from '../../../domain/reading';

// The Family Contacts tab — a list of people, as MMS's is (#176).
//
// The contact details belong to the family, not to the student: the wizard
// files the parent's email on the family and the student's own on the
// student, and these are different people.
//
// One card shape (#319): every card is a `familyContacts` record, including
// the invoice recipient, as in MMS, and the server resolves who to bill from
// the collection (`normaliseFamilyContact` in domain/people.js).
//
// The account card above the list is one field: the family's own `lastName`,
// a property of the account and of no contact — an invoice is addressed to it
// and the families list sorts by it. `EditFamilyDrawer` is its editor.
//
// ── The `⋮` is MMS's, minus one item ───────────────────────────────────────
//
// MMS's card menu is `✎ Edit`, `🗑 Delete`, `👤 Convert to Student`, the same
// three on every card, including a family's only one; ours keeps Delete on a
// last contact too. `Convert to Student` is not here (#176). Nor is MMS's
// `⇄ Change Family`, which moves a student between accounts: what it does to
// the moved student's taught lessons and charges is unchecked (#176), and a
// guess would rewrite a ledger.
export default function FamilyContactsTab({
  student, name, family, familiesLoading, contactsLoading = false, contactsFailed = false, onViewFamily, onEditFamily = null,
  contacts = [], billingContact = null,
  onAddContact = null, onEditContact = null, onRemoveContact = null,
}) {
  // Before the early returns below: a hook after a conditional return is a
  // different hook order on the next render.
  const [openMenu, setOpenMenu] = useState(null);
  const [confirm, setConfirm] = useState(null);

  if (!student.familyId) {
    return (
      <Card title={`${name}'s Family Contacts`}>
        <div className={s.muted}>This student is not attached to a family account.</div>
      </Card>
    );
  }

  if (familiesLoading && !family) {
    return (
      <Card title={`${name}'s Family Contacts`}>
        <div className={s.muted}>Loading the family account…</div>
      </Card>
    );
  }

  if (!family) {
    // Distinguished from "no family": the student points at a family record
    // that is not there, a broken link worth naming.
    return (
      <Card title={`${name}'s Family Contacts`}>
        <div className={s.broken}>
          This student is attached to a family account that no longer exists.
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* The account itself, and the one field on it a teacher edits: an
          invoice is addressed to the family, whoever it is posted to. MMS has
          no such card — its account name is derived from its contact —
          and `EditFamilyDrawer`'s header argues why ours is stored. */}
      <Card
        title={`${name}'s Family Account`}
        action={onEditFamily ? (
          <EditPencil label="Edit Family Account" onClick={onEditFamily} />
        ) : null}
      >
        <DetailRow label="Family Name" value={family.lastName} />
        {/* Who the studio's post to this account goes to, named rather than
            inferred from the badges below. `familyBillingContact` in
            domain/people.js decides it, including the tie-break for a family
            that has flagged two contacts, which MMS permits and does not
            resolve. A family with no contacts says so here: an invoice
            reminder then reaches nobody. */}
        <DetailRow
          label="Billed to"
          value={billingContact ? contactCardName(billingContact) : null}
        />
        {contacts.length === 0 && !contactsLoading ? (
          <div className={s.note}>{RW['family.noContacts']}</div>
        ) : null}
        {/* The contacts' read stopped before it answered (#647). */}
        {contacts.length === 0 && contactsLoading && contactsFailed ? <ReadFailed inline /> : null}

        <div className={s.actions}>
          <Button variant="secondary" onClick={onViewFamily}>View Family Account</Button>
        </div>
      </Card>

      {/* Above the list and below the account card, where MMS puts its
          `＋ Add Contact`, so a family with no contacts can still gain one. */}
      {onAddContact ? (
        <div className={s.addRow}>
          <Button variant="secondary" onClick={onAddContact}>＋ Add Contact</Button>
        </div>
      ) : null}

      {contacts.map((contact) => (
        <Card
          key={contact.id}
          title={contactCardName(contact)}
          action={(
            <Menu
              open={openMenu === contact.id}
              onClose={() => setOpenMenu(null)}
              trigger={(
                // A `⋮`, as MMS's card menu is a kebab; the same control as the
                // category list's row menu.
                <button
                  type="button"
                  className={s.kebab}
                  aria-label={`Actions for ${contactCardName(contact)}`}
                  onClick={() => setOpenMenu((c) => (c === contact.id ? null : contact.id))}
                >
                  ⋮
                </button>
              )}
            >
              {onEditContact ? (
                <MenuItem icon="pencil" onClick={() => { setOpenMenu(null); onEditContact(contact); }}>
                  Edit
                </MenuItem>
              ) : null}
              {onRemoveContact ? (
                <MenuItem icon="trash" onClick={() => { setOpenMenu(null); setConfirm(contact); }}>
                  Delete
                </MenuItem>
              ) : null}
            </Menu>
          )}
        >
          <div className={s.contactHead}>
            {contact.isInvoiceRecipient ? (
              <span className={s.badge}>Invoice Recipient</span>
            ) : null}
            {/* Ours, not MMS's: MMS lets two contacts carry the badge above and
                never chooses between them; our server does, so the one it chose
                is marked. The account card's `Billed to` row says it in words. */}
            {contact.id && contact.id === billingContact?.id ? (
              <span className={s.badgeBilled}>Billed</span>
            ) : null}
          </div>

          <DetailRow label="Email" value={contact.email} />
          <DetailRow label="Address" value={contact.address} />
          {/* The three numbers in MMS's order, and an empty one is not drawn:
              two empty rows for the ordinary contact who gave one number would
              read as a rendering fault. */}
          {CONTACT_NUMBERS.map(({ key, sms, label }) => (contact[key] ? (
            <React.Fragment key={key}>
              <DetailRow label={label} value={contact[key]} />
              <div className={s.note}>
                {contact[sms] ? '✓' : '✕'} Can receive text messages
              </div>
            </React.Fragment>
          ) : null))}
          {contact.note ? <div className={s.note}>{contact.note}</div> : null}
        </Card>
      ))}

      {/* MMS's delete is ungated and says nothing about what the contact is
          on, and nothing hangs off a contact here either. The wording follows
          the tag editor's, from MMS's own confirm for a tag (#138). */}
      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete Contact"
        message={`Are you sure you want to delete ${contactCardName(confirm)} from this family?`}
        confirmLabel="Delete"
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={() => setConfirm(null)}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const target = confirm;
          setConfirm(null);
          await onRemoveContact(target.id);
        }}
      />
    </>
  );
}
